import type { EndUser, Integration } from "@amplitude/engagement-browser";

import { extractUserProperties, trackEvent } from "./analytics";
import { getRbacClaims } from "./auth";
import env from "./env";
import { isImpersonating } from "./impersonation";

/**
 * The event the SDK hands to an integration, taken from the package's own
 * `Integration` rather than from `@amplitude/analytics-types`. That package is
 * a transitive dependency of the SDK, not one of ours, so importing it directly
 * would reach through the dependency tree for a type the SDK already exposes.
 */
type EngagementEvent = Parameters<NonNullable<Integration["track"]>>[0];

/**
 * Which app of the HG platform these guides belong to.
 *
 * The same value the Segment events already carry, so a guide scoped to Market
 * Analyzer and a chart counting Market Analyzer describe the same product. The
 * Amplitude project is shared across the platform, so an announcement without
 * this is one every app shows.
 */
const APP = "market-analyzer";

/**
 * Which Amplitude data centre the project lives in. A constant rather than an
 * environment variable because it is a property of the Amplitude project, not
 * of a deployment, and a build pointed at the wrong zone would silently find no
 * guides at all rather than fail.
 */
const SERVER_ZONE = "US";

/**
 * How often the SDK re-reads what it is allowed to show.
 *
 * Left unset, auto-refresh is off and the SDK reads the user's interaction
 * state once, at boot. That state holds "already dismissed", "already
 * completed" and the throttle counters, and this is an app people leave open
 * all day: without a refresh, an announcement dismissed in another tab shows
 * again here, and a throttle spent elsewhere is not seen. Hourly is Amplitude's
 * own example and well below the rate any of it changes; the SDK's floor is 60
 * seconds. Throttling itself stays configured in Amplitude.
 */
const AUTO_REFRESH_INTERVAL_SECONDS = 3600;

/**
 * Whether the loader has been installed on `window.engagement` for this
 * document. `init` is idempotent by contract, but it also injects the CDN
 * script tag, so a second call is a second request for nothing.
 */
let initialized = false;

/**
 * Whether a boot has been issued. Set before the boot resolves, so a sign-out
 * still shuts down a boot that is in flight.
 */
let booted = false;

/**
 * Which start is current. A start resolves a dynamic import before it can touch
 * the SDK, and by then the session it was started for may already be gone.
 */
let generation = 0;

/**
 * Builds the user a guide is targeted at and conditioned on.
 *
 * Amplitude evaluates a guide's Conditional Logic against the properties the
 * client shared during the session, and nothing else: these same facts already
 * reach Amplitude through Segment, but a condition cannot read those, so a
 * property that only ever travelled server-side reads as absent and the
 * condition never matches.
 *
 * Keyed by the MadKudu user ObjectId for the reason the Segment identify is
 * (CPF-1199), and here it matters twice over: Amplitude keys what a user has
 * already seen, dismissed and rage closed to this id, so an unstable one makes
 * every visit a new user who has seen nothing.
 *
 * @returns The user to boot with, or null when there is no stable id to use.
 */
function toEndUser(): EndUser | null {
  const { userId, tenant } = extractUserProperties();
  if (!userId) {
    return null;
  }

  return {
    user_id: userId,
    user_properties: {
      app: APP,
      tenant,
      isInternalUser: getRbacClaims().superKudu,
    },
  };
}

/**
 * Sends one of the SDK's own events down the app's analytics pipeline.
 *
 * The names belong to Amplitude's SDK, which is downloaded from their CDN at
 * runtime, so they are carried through as they arrive. Without this the SDK
 * collects its guide impressions in the browser and reports none of them.
 *
 * @param event - The event the SDK tracked.
 */
function forwardToSegment(event: EngagementEvent): void {
  const properties = event.event_properties;
  trackEvent(event.event_type, properties ? { ...properties } : {});
}

/**
 * Loads the SDK and makes guides available to the signed-in user.
 *
 * No key means the feature is off, which is the state of every environment not
 * provisioned yet. The import is dynamic so an unprovisioned build never
 * downloads the loader, and so the CDN script tag is injected once the session
 * is known rather than while the app is still starting.
 *
 * Nothing runs while god mode is impersonating. Impersonation here replaces the
 * access token with the target's, so the booted id would be the customer's, and
 * every announcement an HG user opened and closed during a support session
 * would be spent against that customer's record. Strike detection makes it
 * worse rather than better: a support session is exactly where a popup gets
 * closed fast, which is the signal Amplitude reads as the guide being
 * unwelcome.
 *
 * Nothing here can reject into the caller. A blocked CDN, an ad blocker or a
 * failed boot leaves the app without guides, which is not a reason to break the
 * page that was meant to show them.
 */
export async function startGuides(): Promise<void> {
  if (!env.AMPLITUDE_API_KEY || isImpersonating()) {
    return;
  }

  const user = toEndUser();
  if (!user) {
    return;
  }

  const started = (generation += 1);

  try {
    const { init } = await import("@amplitude/engagement-browser");
    if (started !== generation) {
      return;
    }

    if (!initialized) {
      init(env.AMPLITUDE_API_KEY, {
        serverZone: SERVER_ZONE,
        autoRefreshIntervalSeconds: AUTO_REFRESH_INTERVAL_SECONDS,
      });
      initialized = true;
    }

    booted = true;
    await window.engagement.boot({
      user,
      integrations: [{ track: forwardToSegment }],
    });
  } catch {
    return;
  }
}

/**
 * Takes the guides away again, and cancels any start still resolving.
 *
 * Called on sign-out beside the Segment reset, so the next session on the same
 * browser is not offered the previous user's announcements.
 */
export function stopGuides(): void {
  generation += 1;
  if (!booted) {
    return;
  }
  booted = false;

  try {
    window.engagement.shutdown();
  } catch {
    return;
  }
}
